/** Platform → Agencies (D-148, super-admin only): create a new agency (name, sub-domain, country,
 * language, time zone, currency), invite its owner by a secret link, follow each onboarding. */

import {
  getPlatformAgenciesListQueryKey,
  useAuthMeRetrieve,
  usePlatformAgenciesCreate,
  usePlatformAgenciesList,
} from "@agencyos/api-client";
import { Badge, Button, GlassCard, SelectField, TextField } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { Page } from "../../components/AppShell";
import { fieldErrors } from "../../lib/settings";
import { formatDuration } from "./format";
import "./onboarding.css";

const EMPTY = {
  name: "",
  slug: "",
  country: "CM",
  language: "fr" as "fr" | "en",
  timezone: "Africa/Douala",
  currency: "XAF",
  owner_email: "",
  owner_name: "",
};
type Field = Exclude<keyof typeof EMPTY, "language">;
const FIELDS: [Field, string][] = [
  ["name", "onboarding.platform.name"],
  ["slug", "onboarding.platform.slug"],
  ["country", "onboarding.platform.country"],
  ["timezone", "onboarding.platform.timezone"],
  ["currency", "onboarding.platform.currency"],
  ["owner_email", "onboarding.platform.ownerEmail"],
  ["owner_name", "onboarding.platform.ownerName"],
];

/** « Sahel Voyages » → « sahel-voyages » (a suggestion; the API checks the sub-domain). */
function suggestSlug(name: string): string {
  return name
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 63);
}

export function PlatformAgenciesPage() {
  const { t } = useTranslation();
  const me = useAuthMeRetrieve();
  const isSuperuser = me.data?.status === 200 && me.data.data.is_superuser;
  const queryClient = useQueryClient();
  const agencies = usePlatformAgenciesList({ query: { enabled: isSuperuser } });
  const rows = agencies.data?.status === 200 ? agencies.data.data : [];
  const [values, setValues] = useState(EMPTY);
  const [slugTouched, setSlugTouched] = useState(false);
  const create = usePlatformAgenciesCreate({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getPlatformAgenciesListQueryKey() });
        setValues(EMPTY);
        setSlugTouched(false);
      },
    },
  });
  const created = create.data?.status === 201 ? create.data.data : undefined;
  const errors = fieldErrors(create.error);

  if (me.data && !isSuperuser) {
    return (
      <Page title={t("onboarding.platform.title")}>
        <p role="alert" className="ko">
          {t("onboarding.platform.forbidden")}
        </p>
      </Page>
    );
  }

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate({ data: values });
  };
  const set = (field: Field, value: string) => {
    if (field === "slug") setSlugTouched(true);
    const next = { ...values, [field]: value };
    if (field === "name" && !slugTouched) next.slug = suggestSlug(value);
    setValues(next);
  };

  return (
    <Page title={t("onboarding.platform.title")} description={t("onboarding.platform.intro")}>
      <GlassCard className="stack" aria-labelledby="platform-new-agency">
        <h2 id="platform-new-agency">{t("onboarding.platform.create")}</h2>
        <form className="stack" onSubmit={onSubmit}>
          <div className="onboarding__grid">
            {FIELDS.map(([field, label]) => (
              <TextField
                key={field}
                label={t(label)}
                type={field === "owner_email" ? "email" : "text"}
                required={["name", "slug", "owner_email"].includes(field)}
                hint={field === "slug" ? t("onboarding.platform.slugHint") : undefined}
                value={values[field]}
                error={errors[field]}
                onChange={(e) => set(field, e.target.value)}
              />
            ))}
            <SelectField
              label={t("onboarding.platform.language")}
              value={values.language}
              options={(["fr", "en"] as const).map((code) => ({ value: code, label: t(`onboarding.platform.languages.${code}`) }))}
              onChange={(e) => setValues({ ...values, language: e.target.value === "en" ? "en" : "fr" })}
            />
          </div>
          <div>
            <Button type="submit" loading={create.isPending}>
              {t("onboarding.platform.submit")}
            </Button>
          </div>
          {created ? <p className="ok">{t("onboarding.platform.created", { email: created.owner_email })}</p> : null}
        </form>
      </GlassCard>

      <GlassCard className="stack" aria-labelledby="platform-agencies">
        <h2 id="platform-agencies">{t("onboarding.platform.list")}</h2>
        {rows.length === 0 ? (
          <p className="muted">{t("onboarding.platform.empty")}</p>
        ) : (
          <ul className="onboarding__agencies">
            {rows.map((row) => (
              <li key={row.id}>
                <div>
                  <strong>{row.name}</strong>
                  <a href={row.site_url} target="_blank" rel="noreferrer" className="muted">
                    {row.site_url}
                  </a>
                  {row.owner_email ? <small className="muted">{row.owner_email}</small> : null}
                </div>
                <Badge tone={row.completed_at ? "success" : "info"}>
                  {row.completed_at && row.elapsed_seconds !== null
                    ? t("onboarding.platform.completed", { time: formatDuration(row.elapsed_seconds) })
                    : t("onboarding.platform.inProgress")}
                </Badge>
                <span className="muted">{t("onboarding.platform.steps", { done: row.steps_done, total: row.steps_total })}</span>
              </li>
            ))}
          </ul>
        )}
      </GlassCard>
    </Page>
  );
}
